import { ColumnDef } from '@tanstack/react-table';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardCheckbox from 'base/StandardCheckbox/StandardCheckbox';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { t } from 'i18next';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import './ProductTableModal.scss';
import { capitalizeFirstLetter } from 'utils/helpers';
import { StandardLoader } from 'base/Loader/Loader';

interface ProductTableModalI<U> {
  showPopup: boolean;
  setShowPopup: React.Dispatch<React.SetStateAction<boolean>>;
  successFunction?: () => void;
  showTable?: boolean;
  optionTable?: U[];
  getData: (data: U[]) => void;
  id?: U[];
  headText?: string;
  title?: string;
  loading: boolean;
}

export const ProductTableModal = <
  U extends { product_id: number; name: string }
>(
  props: ProductTableModalI<U>
) => {
  const [selectedItems, setSelectedItems] = useState<U[]>(props.id || []);

  const handleCheck = (isChecked: boolean, rowData: U) => {
    let updatedArray = [...selectedItems];

    if (isChecked) {
      updatedArray = [
        rowData,
        ...updatedArray.filter(item => item.product_id !== rowData.product_id)
      ];
    } else {
      updatedArray = updatedArray.filter(
        item => item.product_id !== rowData.product_id
      );
    }

    setSelectedItems(updatedArray);
  };

  const handleSubmit = () => {
    props.getData(selectedItems);
    props.setShowPopup(false);
  };

  useEffect(() => {
    setSelectedItems(props.id || []);
  }, [props.id]);

  const columnTableList: ColumnDef<U>[] = [
    {
      accessorKey: t('applications.table.title_1'),
      cell: ({ row: { index } }) => index + 1
    },
    {
      accessorKey: `${props.title}`,
      cell: ({ row: { original } }) => {
        return <div>{original.name}</div>;
      }
    },
    {
      accessorKey: `${t('general.common_text.text_8')}`,
      cell: ({ row: { original, index } }) => {
        const exists = selectedItems.some(
          item => item.product_id === original.product_id
        );
        return (
          <StandardCheckbox
            key={index}
            isChecked={exists}
            setChecked={(isChecked: boolean) =>
              handleCheck(isChecked, original)
            }
            label=""
            className=""
            disabled={false}
          />
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: props.optionTable || [],
    columns: columnTableList,
    selection: false,
    sortable: false,
    pageSize: 999,
    pagination: true
  });

  return (
    <Modal
      centered
      size="lg"
      show={props.showPopup}
      dialogClassName="standard-popup"
      id="application_type_option_model"
      onHide={() => props.setShowPopup(false)}
    >
      <Modal.Header className="p-3" closeButton>
        <h3 className="text-gray-color font-normal fs-22 font-semi-bold justify-content-center d-flex align-items-center mb-0">
          {capitalizeFirstLetter(props.headText as string)}
        </h3>
      </Modal.Header>
      <ConditionalRender
        condition={!props.loading}
        fallBack={<StandardLoader />}
      >
        <Modal.Body className="p-4 overflow-auto scrollbar">
          <ConditionalRender condition={!!props.showTable}>
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  size: 'sm',
                  className: 'phoenix-table fs-9 mb-0 border-translucent',
                  borderless: true,
                  responsive: 'lg'
                }}
                rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
              />
            </AdvanceTableProvider>
          </ConditionalRender>
        </Modal.Body>
        <Modal.Footer className="text-start p-2 d-flex justify-content-start">
          <div className="d-flex justify-content-end w-100">
            <StandardButton
              buttonText={t('general.common_text.text_15')}
              className="cancel-btn-style me-3"
              variant="outline-danger"
              onClick={() => props.setShowPopup(false)}
            />
            <StandardButton
              buttonText={t('general.common_text.text_2')}
              className="add-btn-style"
              onClick={handleSubmit}
            />
          </div>
        </Modal.Footer>
      </ConditionalRender>
    </Modal>
  );
};
